"use client";

import React, { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { getCountries, getUserCountry, type Country } from "@/lib/services/country-service";
import Image from "next/image";

interface CountryPickerProps {
  value?: Country | null;
  onChange: (country: Country) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  autoDetect?: boolean;
}

export default function CountryPicker({
  value,
  onChange,
  placeholder,
  label,
  error,
  autoDetect = true,
}: CountryPickerProps) {
  const t = useTranslations("general");
  const [isOpen, setIsOpen] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isDetecting, setIsDetecting] = useState(autoDetect && !value);

  // Load countries and auto-detect
  useEffect(() => {
    const initializeCountry = async () => {
      setIsLoading(true);
      try {
        // Load countries list
        const countriesList = await getCountries();
        setCountries(countriesList);
        setFilteredCountries(countriesList);

        // Auto-detect country if enabled and no value set
        if (autoDetect && !value) {
          setIsDetecting(true);
          const detectedCountry = await getUserCountry();
          if (detectedCountry) {
            onChange(detectedCountry);
          } else {
            // Default to Egypt (target country for first phase) if detection fails
            const egyptCountry = countriesList.find(c => c.name.common === "Egypt");
            if (egyptCountry) {
              onChange(egyptCountry);
            } else if (countriesList.length > 0) {
              // Fallback to first country if Egypt not found
              onChange(countriesList[0]);
            }
          }
          setIsDetecting(false);
        }
      } catch (error) {
        console.error("Error initializing country picker:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeCountry();
  }, [autoDetect, value, onChange]);

  // Filter countries based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCountries(countries);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = countries.filter((country) =>
      country.name.common.toLowerCase().includes(query)
    );
    setFilteredCountries(filtered);
  }, [searchQuery, countries]);

  const handleSelectCountry = (country: Country) => {
    onChange(country);
    setIsOpen(false);
    setSearchQuery("");
  };

  const displayValue = value?.name.common || "";
  const displayPlaceholder = isDetecting
    ? t("detecting_location") || "Detecting location..."
    : placeholder || t("select_location") || "Select your location";

  return (
    <div className="space-y-2">
      {label && (
        <label className="text-sm font-medium text-primary-foreground/70">
          {label}
        </label>
      )}
      <Button
        type="button"
        variant="outline"
        onClick={() => setIsOpen(true)}
        className={cn(
          "w-full justify-between rounded-full bg-black font-poppins border-[#595959] py-5 sm:py-6 px-6 text-left font-normal text-primary-foreground hover:bg-black/80 text-sm sm:text-base",
          error && "border-red-500",
          isDetecting && "text-primary-foreground/50"
        )}
        disabled={isDetecting || isLoading}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {isDetecting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin text-primary flex-shrink-0" />
              <span className="truncate text-primary-foreground/50">
                {displayPlaceholder}
              </span>
            </>
          ) : value ? (
            <>
              <Image
                src={value.flags.png}
                alt={value.name.common}
                width={24}
                height={18}
                className="rounded-sm flex-shrink-0"
              />
              <span className="truncate">{displayValue}</span>
            </>
          ) : (
            <span className="truncate text-primary-foreground/50">
              {displayPlaceholder}
            </span>
          )}
        </div>
        <ChevronDown className="h-4 w-4 flex-shrink-0 ml-2 text-primary-foreground/50" />
      </Button>
      {error && (
        <p className="text-xs text-red-500 font-poppins">{error}</p>
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[500px] bg-black/95 border-white/20">
          <DialogHeader>
            <DialogTitle className="text-white">
              {t("select_location") || "Select your location"}
            </DialogTitle>
          </DialogHeader>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary-foreground/40" />
            <Input
              placeholder={t("search") || "Search countries..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-black/50 border-white/20 text-white placeholder:text-primary-foreground/40"
            />
          </div>

          {/* Countries List */}
          <ScrollArea className="h-[400px] mt-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : filteredCountries.length === 0 ? (
              <div className="text-center py-12 text-primary-foreground/60">
                {t("select_a_location_to_continue") || "No countries found"}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredCountries.map((country) => (
                  <button
                    key={country.name.common}
                    type="button"
                    onClick={() => handleSelectCountry(country)}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left group",
                      value?.name.common === country.name.common
                        ? "bg-gradient-to-r from-primary/20 to-second-primary/20 border border-primary/30"
                        : "hover:bg-white/5"
                    )}
                  >
                    <Image
                      src={country.flags.png}
                      alt={country.name.common}
                      width={32}
                      height={24}
                      className="rounded-sm flex-shrink-0"
                    />
                    <span className="text-primary-foreground group-hover:text-white flex-1 transition-colors">
                      {country.name.common}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

